import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import FacebookLogin from '@greatsumini/react-facebook-login';
import { loginWithFacebook, loginWithGoogle } from '../../api/auth';
import { getErrorMessage } from '../../api/errors';
import { pushDataLayer } from '../../lib/measure';
import { useSession } from '../../state/session';
import { Alert } from '../../ui';
import styles from './auth.module.css';

const GOOGLE_CLIENT_ID = '772231057030-flg3va6slv0d0acmapb7n0bc20nrf57p.apps.googleusercontent.com';
const FACEBOOK_APP_ID = '380457629699012';

function FacebookMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.socialIcon}>
      <path
        fill="#1877F2"
        d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.88v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"
      />
    </svg>
  );
}

/** Google + Facebook sign-in. Both create the account on first use. */
export function SocialAuth({ onSuccess }) {
  const { signIn } = useSession();
  const boxRef = useRef(null);
  const [width, setWidth] = useState(360);

  useEffect(() => {
    const node = boxRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.max(200, Math.min(400, Math.floor(entry.contentRect.width))));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const finish = (method) => (user) => {
    signIn(user);
    pushDataLayer({ event: 'account_created', method });
    onSuccess?.(user);
  };

  const google = useMutation({ mutationFn: loginWithGoogle, onSuccess: finish('google') });
  const facebook = useMutation({ mutationFn: loginWithFacebook, onSuccess: finish('facebook') });
  const error = google.error || facebook.error;

  return (
    <div className={styles.social} ref={boxRef}>
      {error && <Alert tone="danger">{getErrorMessage(error, 'Social sign-in failed. Please try again.')}</Alert>}
      <div className={styles.googleSlot}>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <GoogleLogin
            width={width}
            text="continue_with"
            shape="rectangular"
            logo_alignment="center"
            onSuccess={(response) => google.mutate(response.credential)}
            onError={() => {}}
          />
        </GoogleOAuthProvider>
      </div>
      <FacebookLogin
        appId={FACEBOOK_APP_ID}
        onSuccess={(response) => {
          if (response?.accessToken) facebook.mutate(response.accessToken);
        }}
        render={({ onClick }) => (
          <button type="button" className={styles.socialButton} onClick={onClick} disabled={facebook.isPending}>
            <FacebookMark />
            Continue with Facebook
          </button>
        )}
      />
    </div>
  );
}
